import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Building2,
  GraduationCap,
  Award,
  Target,
  Users,
  Heart,
  Lightbulb,
  Globe,
  Mail,
  Bell,
  Star,
  ArrowRight,
  Sparkles,
  BookOpen,
  Briefcase,
  MapPin,
  Calendar,
  CheckCircle,
} from 'lucide-react';
import { Button } from 'src/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/Card';
import { Badge } from 'src/components/ui/Badge';
import { Input } from 'src/components/ui/Input';
import Navbar from 'src/components/Navbar';
import Footer from 'src/components/Footer';

const learningPrinciples = [
  {
    label: 'personalized learning experiences',
    placement: 'principle-bubble--one',
  },
  {
    label: 'mastery-based learning',
    placement: 'principle-bubble--two',
  },
  {
    label: 'immediate feedback',
    placement: 'principle-bubble--three',
  },
  {
    label: 'progressive level of difficulty',
    placement: 'principle-bubble--four',
  },
  {
    label: 'progressive hints',
    placement: 'principle-bubble--five',
  },
];

const About = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNotifyMe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      console.log('Subscribed email:', email);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative isolate min-h-screen overflow-hidden px-4 pb-16 pt-20 text-center md:mt-20 md:pb-20 md:pt-24">
        {/* Content Container */}
        <div className="relative z-10 mx-auto mb-10 max-w-4xl md:mb-12">
          <h1 className="mb-5 text-5xl font-bold tracking-tight text-gray-950 md:text-6xl">
            About <span className="text-blue-700">rfLearn</span>
          </h1>
          <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 md:text-xl">
            Revolutionizing education through personalized learning experiences, expert tutoring,
            and cutting-edge technology. Founded with a vision to make quality education accessible to everyone.
          </p>
        </div>

        {/* Flowing River Animation */}
        <div className="relative mx-auto h-[25rem] max-w-6xl overflow-hidden rounded-[2rem] shadow-[0_28px_65px_-28px_rgba(30,64,175,0.55)] md:h-[27rem]">
          {/* River Container */}
          <div className="water-surface relative w-full h-full bg-gradient-to-b from-cyan-200 via-blue-400 to-blue-800">

            {/* Water Surface Waves */}
            <div className="absolute top-0 w-full h-4 bg-gradient-to-b from-cyan-100 to-transparent opacity-40">
              <div className="wave-1 absolute top-0 w-full h-full"></div>
              <div className="wave-2 absolute top-0 w-full h-full"></div>
              <div className="wave-3 absolute top-0 w-full h-full"></div>
              <div className="wave-4 absolute top-0 w-full h-full"></div>
            </div>

            {/* Flowing Water Effect */}
            <div className="absolute inset-0 opacity-30">
              <div className="flowing-water-1 absolute w-full h-full bg-gradient-to-r from-transparent via-white to-transparent"></div>
              <div className="flowing-water-2 absolute w-full h-full bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
              <div className="flowing-water-3 absolute w-full h-full bg-gradient-to-r from-transparent via-white to-transparent"></div>
            </div>

            {/* River Ripples */}
            <div className="absolute inset-0">
              <div className="ripple-1 absolute w-16 h-16 border-2 border-white opacity-30 rounded-full"></div>
              <div className="ripple-2 absolute w-12 h-12 border border-white opacity-25 rounded-full"></div>
              <div className="ripple-3 absolute w-20 h-20 border-2 border-blue-100 opacity-20 rounded-full"></div>
              <div className="ripple-4 absolute w-14 h-14 border border-blue-200 opacity-35 rounded-full"></div>
              <div className="ripple-5 absolute w-18 h-18 border border-white opacity-15 rounded-full"></div>
              <div className="ripple-6 absolute w-10 h-10 border border-blue-100 opacity-40 rounded-full"></div>
            </div>

            <div className="absolute inset-0 z-10" aria-label="How rfLearn supports learning">
              {learningPrinciples.map((principle) => (
                <div
                  key={principle.label}
                  className={`principle-bubble ${principle.placement}`}
                >
                  {principle.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes wave1 {
            0% {
              transform: translateX(-100%);
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            }
            50% {
              transform: translateX(0%);
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
            }
            100% {
              transform: translateX(100%);
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            }
          }

          @keyframes wave2 {
            0% {
              transform: translateX(100%) skewX(-5deg);
              background: linear-gradient(90deg, transparent, rgba(200,230,255,0.5), transparent);
            }
            50% {
              transform: translateX(0%) skewX(0deg);
              background: linear-gradient(90deg, transparent, rgba(200,230,255,0.7), transparent);
            }
            100% {
              transform: translateX(-100%) skewX(5deg);
              background: linear-gradient(90deg, transparent, rgba(200,230,255,0.5), transparent);
            }
          }

          @keyframes wave3 {
            0% {
              transform: translateX(-50%) rotate(1deg);
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
            }
            50% {
              transform: translateX(50%) rotate(-1deg);
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
            }
            100% {
              transform: translateX(150%) rotate(1deg);
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
            }
          }

          @keyframes wave4 {
            0% {
              transform: translateX(150%);
              background: linear-gradient(90deg, transparent, rgba(180,220,255,0.4), transparent);
            }
            50% {
              transform: translateX(25%);
              background: linear-gradient(90deg, transparent, rgba(180,220,255,0.6), transparent);
            }
            100% {
              transform: translateX(-100%);
              background: linear-gradient(90deg, transparent, rgba(180,220,255,0.4), transparent);
            }
          }

          @keyframes waterSurface {
            0% {
              clip-path: polygon(0% 0%, 10% 1%, 20% 0.5%, 30% 1.5%, 40% 0.5%, 50% 2%, 60% 1%, 70% 1.5%, 80% 0.5%, 90% 1%, 100% 0%, 100% 100%, 0% 100%);
            }
            25% {
              clip-path: polygon(0% 0.5%, 10% 1.5%, 20% 0%, 30% 1%, 40% 2%, 50% 0.5%, 60% 1.5%, 70% 0%, 80% 1%, 90% 2%, 100% 0.5%, 100% 100%, 0% 100%);
            }
            50% {
              clip-path: polygon(0% 1%, 10% 0%, 20% 1.5%, 30% 0.5%, 40% 1%, 50% 0%, 60% 2%, 70% 1%, 80% 1.5%, 90% 0.5%, 100% 1%, 100% 100%, 0% 100%);
            }
            75% {
              clip-path: polygon(0% 0.5%, 10% 2%, 20% 1%, 30% 0%, 40% 1.5%, 50% 1%, 60% 0.5%, 70% 2%, 80% 0%, 90% 1.5%, 100% 0.5%, 100% 100%, 0% 100%);
            }
            100% {
              clip-path: polygon(0% 0%, 10% 1%, 20% 0.5%, 30% 1.5%, 40% 0.5%, 50% 2%, 60% 1%, 70% 1.5%, 80% 0.5%, 90% 1%, 100% 0%, 100% 100%, 0% 100%);
            }
          }

          @keyframes flowRight {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }

          @keyframes flowLeft {
            0% { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
          }

          @keyframes ripple {
            0% { transform: scale(0.3); opacity: 0; }
            30% { opacity: 1; }
            100% { transform: scale(1.5); opacity: 0; }
          }

          @keyframes rippleFloat {
            0% { transform: scale(0.5) translateY(0px); opacity: 0; }
            50% { opacity: 1; transform: scale(1) translateY(-5px); }
            100% { transform: scale(1.8) translateY(-10px); opacity: 0; }
          }

          .wave-1 {
            animation: wave1 4s ease-in-out infinite;
            animation-delay: 0s;
          }

          .wave-2 {
            animation: wave2 5.5s ease-in-out infinite;
            animation-delay: -1s;
          }

          .wave-3 {
            animation: wave3 3.5s ease-in-out infinite;
            animation-delay: -2s;
          }

          .wave-4 {
            animation: wave4 6s ease-in-out infinite;
            animation-delay: -3s;
          }

          .water-surface {
            animation: waterSurface 3s ease-in-out infinite;
          }

          .flowing-water-1 {
            animation: flowRight 8s linear infinite;
            animation-delay: 0s;
          }

          .flowing-water-2 {
            animation: flowLeft 6s linear infinite;
            animation-delay: -2s;
          }

          .flowing-water-3 {
            animation: flowRight 10s linear infinite;
            animation-delay: -4s;
          }

          .ripple-1 {
            top: 25%;
            left: 15%;
            animation: ripple 3s ease-out infinite;
            animation-delay: 0s;
          }

          .ripple-2 {
            top: 65%;
            left: 75%;
            animation: rippleFloat 4s ease-out infinite;
            animation-delay: -1.5s;
          }

          .ripple-3 {
            top: 45%;
            left: 35%;
            animation: ripple 5s ease-out infinite;
            animation-delay: -2.5s;
          }

          .ripple-4 {
            top: 75%;
            left: 85%;
            animation: rippleFloat 3.5s ease-out infinite;
            animation-delay: -1s;
          }

          .ripple-5 {
            top: 30%;
            left: 60%;
            animation: ripple 4.5s ease-out infinite;
            animation-delay: -3s;
          }

          .ripple-6 {
            top: 55%;
            left: 20%;
            animation: rippleFloat 3.8s ease-out infinite;
            animation-delay: -0.5s;
          }

          .principle-bubble {
            position: absolute;
            max-width: 14rem;
            color: white;
            font-size: 1rem;
            font-weight: 700;
            line-height: 1.25;
            text-align: left;
            text-shadow: 0 3px 18px rgba(9, 42, 118, 0.72);
            transform-origin: center;
            will-change: transform, opacity;
            animation: bubbleBloom 8s cubic-bezier(0.22, 1, 0.36, 1) infinite;
          }

          .principle-bubble--one { top: 13%; left: 7%; animation-delay: -0.8s; }
          .principle-bubble--two { top: 30%; right: 7%; animation-delay: -2.8s; }
          .principle-bubble--three { bottom: 14%; left: 16%; animation-delay: -4.8s; }
          .principle-bubble--four { right: 15%; bottom: 12%; animation-delay: -6.8s; }
          .principle-bubble--five { top: 45%; left: calc(50% - 4.5rem); width: 9rem; text-align: center; animation-delay: -3.8s; }

          @keyframes bubbleBloom {
            0% { opacity: 0; transform: scale(0.62); }
            12% { opacity: 0.96; transform: scale(0.8); }
            48% { opacity: 1; transform: scale(1); }
            78% { opacity: 0.7; transform: scale(1.22); }
            100% { opacity: 0; transform: scale(1.42); }
          }

          @media (max-width: 767px) {
            .principle-bubble {
              max-width: 9.5rem;
              font-size: 0.8125rem;
            }
            .principle-bubble--one { top: 8%; left: 4%; }
            .principle-bubble--two { top: 31%; right: 3%; }
            .principle-bubble--three { bottom: 26%; left: 3%; }
            .principle-bubble--four { right: 4%; bottom: 4%; }
            .principle-bubble--five { top: 46%; left: calc(50% - 3.5rem); width: 7rem; }
          }

          @media (prefers-reduced-motion: reduce) {
            .water-surface,
            .wave-1,
            .wave-2,
            .wave-3,
            .wave-4,
            .flowing-water-1,
            .flowing-water-2,
            .flowing-water-3,
            .ripple-1,
            .ripple-2,
            .ripple-3,
            .ripple-4,
            .ripple-5,
            .ripple-6,
            .principle-bubble {
              animation: none;
            }
          }
        `}</style>
      </section>

      {/* Founder Section */}
      <section className="bg-[#fff8f5] px-4 py-20 md:py-28">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14 text-center md:mb-16">
            <h2 className="mb-4 text-4xl font-bold tracking-tight text-slate-950 md:text-5xl">Visionary Leadership</h2>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-slate-600 md:text-xl">
              Led by industry expertise and a passion for transforming education
            </p>
          </div>

          {/* Founder Profile Card */}
          <div className="mx-auto max-w-5xl">
            <Card className="overflow-hidden border-0 bg-white shadow-[0_28px_70px_-32px_rgba(15,23,42,0.38)]">
              <div className="md:flex">
                {/* Profile Image Section */}
                <div className="relative flex overflow-hidden bg-[#f7b0a5] p-8 md:w-[35%] md:p-10">
                  <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full border border-white/45" aria-hidden="true" />
                  <div className="absolute -bottom-28 -left-16 h-56 w-56 rounded-full bg-[#e8897d]/35" aria-hidden="true" />
                  <div className="relative z-10 my-auto w-full text-center">
                    <div className="mx-auto mb-6 flex h-32 w-32 items-center justify-center rounded-full border border-white/60 bg-white/30 shadow-[0_18px_34px_-18px_rgba(136,65,61,0.52)]">
                      <User className="h-14 w-14 text-[#5b2b32]" />
                    </div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#804049]">Founder</p>
                    <h3 className="mb-2 text-3xl font-bold tracking-tight text-[#51262e]">Mr. Khan</h3>
                    <p className="font-medium text-[#6f3540]">Founder & CEO</p>
                    <Badge className="mt-5 border-[#9b5057]/25 bg-white/35 px-3 py-1 text-[#643039] hover:bg-white/50">
                      Math Researcher
                    </Badge>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-8 md:w-[65%] md:p-10">
                  <div className="mb-8">
                    <h4 className="mb-4 text-2xl font-semibold tracking-tight text-slate-950">Professional Background</h4>
                    <p className="max-w-2xl text-[1.05rem] leading-8 text-slate-600">
                      Mr. Khan brings extensive experience as a Math Researcher, having worked at some of the world's
                      most innovative technology companies. His expertise spans across mathematical research,
                      educational technology, and strategic planning.
                    </p>
                  </div>

                  {/* Experience Highlights */}
                  <div className="mb-8 grid grid-cols-1 divide-y divide-slate-200 overflow-hidden rounded-xl bg-slate-50 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                    <div className="flex items-center gap-3 px-5 py-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600">
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Meta</p>
                        <p className="text-sm text-slate-500">Math Researcher</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 px-5 py-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800">
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Apple</p>
                        <p className="text-sm text-slate-500">Math Researcher</p>
                      </div>
                    </div>
                  </div>

                  {/* Key Achievements */}
                  <div>
                    <h5 className="mb-3 font-semibold text-slate-950">Key Expertise</h5>
                    <ul className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                      {[
                        "Mathematical Research",
                        "Educational Technology",
                        "Research & Development",
                        "Strategic Planning",
                        "Academic Innovation"
                      ].map((skill, index) => (
                        <li key={index} className="flex items-center gap-3 text-[0.9375rem] font-medium text-slate-700">
                          <span className="h-2 w-2 shrink-0 rounded-full bg-[#dd7a70]" aria-hidden="true" />
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="overflow-hidden bg-white px-4 py-24 md:py-32">
        <div className="mx-auto grid max-w-6xl gap-12 md:grid-cols-2 md:gap-16">
          <article className="rotate-[-2deg] rounded-2xl bg-[#f8b5aa] px-8 py-12 shadow-[0_26px_42px_-30px_rgba(126,59,57,0.58)] md:px-12 md:py-16">
            <div className="rotate-[2deg]">
              <div className="mb-16 h-px w-14 bg-[#9b4b50]" aria-hidden="true" />
              <h2 className="mb-7 max-w-sm text-4xl font-bold tracking-tight text-[#51262e] md:text-5xl">Our Vision</h2>
              <p className="max-w-md text-lg leading-8 text-[#64353b]">
                To create a world where quality education is accessible to everyone, regardless of location or background.
                We envision a future where personalized learning experiences empower students to achieve their full potential.
              </p>
            </div>
          </article>

          <article className="rotate-[2deg] rounded-2xl bg-[#fff7f4] px-8 py-12 shadow-[0_26px_42px_-30px_rgba(89,57,55,0.3)] md:mt-12 md:px-12 md:py-16">
            <div className="rotate-[-2deg]">
              <div className="mb-16 h-px w-14 bg-[#dd7a70]" aria-hidden="true" />
              <h2 className="mb-7 max-w-lg text-4xl font-bold tracking-tight text-slate-950 md:text-5xl">Our Mission</h2>
              <p className="max-w-2xl text-lg leading-8 text-slate-600">
                To revolutionize education by connecting students with expert tutors through innovative technology,
                providing personalized learning paths, and fostering academic excellence in a supportive environment.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-[#fff3ef] px-4 py-20 md:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-4">
              <div className="mb-7 h-px w-14 bg-[#d86f65]" aria-hidden="true" />
              <h2 className="mb-5 text-4xl font-bold tracking-tight text-[#51262e] md:text-5xl">Our Core Values</h2>
              <p className="max-w-sm text-lg leading-8 text-[#6a3b40]">The principles that guide everything we do</p>
            </div>

            <div className="grid gap-x-12 md:col-span-8 md:grid-cols-2">
              {[
                {
                  icon: <GraduationCap className="h-6 w-6" />,
                  title: "Excellence",
                  description: "Committed to delivering the highest quality education and learning experiences",
                },
                {
                  icon: <Users className="h-6 w-6" />,
                  title: "Accessibility",
                  description: "Making quality education available to students everywhere, breaking down barriers",
                },
                {
                  icon: <Lightbulb className="h-6 w-6" />,
                  title: "Innovation",
                  description: "Continuously evolving our platform with cutting-edge technology and methodologies",
                },
                {
                  icon: <Heart className="h-6 w-6" />,
                  title: "Empowerment",
                  description: "Empowering both students and tutors to achieve their goals and make a difference",
                },
              ].map((value) => (
                <article key={value.title} className="border-t border-[#e7b7ad] py-7">
                  <div className="mb-8 text-[#b34e4f]" aria-hidden="true">{value.icon}</div>
                  <h3 className="mb-3 text-2xl font-semibold tracking-tight text-[#51262e]">{value.title}</h3>
                  <p className="max-w-xs text-base leading-7 text-[#70454a]">{value.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            More Coming Soon
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story Continues</h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            We're working on sharing more details about our journey, team members, company milestones,
            and the exciting future we're building together. Stay tuned for updates!
          </p>

          {/* Notify Me Form */}
          <div className="max-w-md mx-auto mb-8">
            {!isSubscribed ? (
              <form onSubmit={handleNotifyMe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1"
                />
                <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </Button>
              </form>
            ) : (
              <div className="bg-green-100 text-green-800 px-6 py-3 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>Thanks! We'll keep you updated on our journey.</span>
              </div>
            )}
            <p className="text-sm text-gray-500 mt-2">
              Be the first to know about company updates and milestones
            </p>
          </div>

          {/* Preview of upcoming content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <Users className="w-5 h-5" />,
                title: "Team Profiles",
                description: "Meet the talented individuals behind RFLearn"
              },
              {
                icon: <Calendar className="w-5 h-5" />,
                title: "Company Timeline",
                description: "Key milestones and achievements in our journey"
              },
              {
                icon: <Award className="w-5 h-5" />,
                title: "Recognition",
                description: "Awards and recognition we've received"
              }
            ].map((item, index) => (
              <Card key={index} className="border-dashed border-2 border-gray-300 bg-white/50">
                <CardHeader className="text-center">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-2">
                    {item.icon}
                  </div>
                  <CardTitle className="text-sm text-gray-600">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Join Our Mission</h2>
          <p className="text-xl mb-8 opacity-90">
            Be part of the educational revolution. Whether you're a student seeking knowledge or a tutor sharing expertise,
            we welcome you to our community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/register" className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                Start Learning
              </Link>
            </Button>
            <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-colors">
              <Link to="/become-tutor" className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Become a Tutor
              </Link>
            </Button>
          </div>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
